import * as React from "react";
import { CalendarIcon, Clock } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { Button } from "@/components/atoms/button";
import { Calendar } from "@/components/atoms/calendar";
import { Input } from "@/components/atoms/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/atoms/popover";

interface DateTimePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
}

// Helper function to parse datetime-local format (YYYY-MM-DDTHH:mm)
const parseDateTime = (
  dateTimeString: string,
): { date: Date | undefined; time: string } => {
  if (!dateTimeString) return { date: undefined, time: "" };

  const [datePart, timePart = ""] = dateTimeString.split("T");

  if (!datePart) return { date: undefined, time: timePart };

  const date = new Date(datePart + "T00:00:00");
  return {
    date: isNaN(date.getTime()) ? undefined : date,
    time: timePart || "",
  };
};

// Helper function to format date and time back to datetime-local format
const formatDateTime = (date: Date | undefined, time: string): string => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}T${time || "00:00"}`;
};

export function DateTimePicker({
  value,
  onChange,
  placeholder = "Seleccionar fecha y hora",
  className,
  disabled = false,
  id,
}: DateTimePickerProps) {
  const { date: initialDate, time: initialTime } = parseDateTime(value || "");

  const [date, setDate] = React.useState<Date | undefined>(initialDate);
  const [time, setTime] = React.useState<string>(initialTime);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const { date: newDate, time: newTime } = parseDateTime(value || "");
    setDate(newDate);
    setTime(newTime);
  }, [value]);

  const handleDateSelect = React.useCallback(
    (selectedDate: Date | undefined) => {
      setDate(selectedDate);
      if (onChange) {
        onChange(formatDateTime(selectedDate, time));
      }
      setOpen(false);
    },
    [onChange, time],
  );

  const handleTimeChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTime = e.target.value;
      setTime(newTime);
      if (onChange) {
        onChange(formatDateTime(date, newTime));
      }
    },
    [onChange, date],
  );

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      if (!disabled) {
        setOpen(newOpen);
      }
    },
    [disabled],
  );

  const displayValue = React.useMemo(() => {
    if (!date) return "";

    const dateStr = format(date, "dd MMM yyyy", { locale: es });
    const timeStr = time ? ` ${time}` : "";
    return `${dateStr}${timeStr}`;
  }, [date, time]);

  return (
    <div className={cn("relative", className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal h-10 md:h-11 lg:h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl transition-all duration-200 hover:border-gray-300 text-sm md:text-base",
              !date && "text-gray-400",
              disabled && "cursor-not-allowed opacity-50",
            )}
            disabled={disabled}
            type="button"
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="truncate">{displayValue || placeholder}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto bg-white p-0 shadow-lg border border-gray-200 rounded-xl z-[1000]"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <div className="p-4 space-y-4">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateSelect}
              initialFocus
              className="rounded-md border-0"
              classNames={{
                day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 data-[selected=true]:bg-blue-600 data-[selected=true]:text-white",
                nav_button:
                  "h-7 w-7 bg-transparent p-0 hover:bg-gray-100 rounded-md cursor-pointer",
                nav_button_previous: "absolute left-1",
                nav_button_next: "absolute right-1",
                day_selected:
                  "bg-blue-600 text-white hover:bg-blue-700 hover:text-white focus:bg-blue-600 focus:text-white",
                day_today: "bg-accent text-accent-foreground font-semibold",
              }}
            />

            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <label className="text-sm font-medium text-gray-700">
                  Hora:
                </label>
              </div>
              <Input
                type="time"
                value={time}
                onChange={handleTimeChange}
                className="mt-2 h-10 bg-gray-50 border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-lg"
                placeholder="00:00"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
